import { effect, inject, Injectable } from '@angular/core';
import { GlobalWebsocketService } from '@approot/global-websocket';
import {
  AlertsService,
  ConfigService,
  UserStorageService
} from '@approot/services';
import { EntityMapperService } from '@approot/services/entity-mapper/entity-mapper.service';
import { clone, sleep } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Row, RunningState, WSRow } from '../shared/utils';
import { mapHeaders } from './events-blotter.funcs';
import { EventsBlotterState } from './events-blotter.types';

// Note that "entities" and "headers" are not included here as they won't change once set.
const DefaultEventsBlotterState: EventsBlotterState = {
  uuid: null,
  rows: [],
  nItems: null,
  runningState: RunningState.Idle,
  info: null
};

@Injectable({
  providedIn: 'root'
})
export class EventsBlotterService {
  public stateChanged: Observable<EventsBlotterState>;
  private state: EventsBlotterState = {};
  // this servivce is permanent but the component is recreated on each tab navigation
  // so we need to use a ReplaySubject to keep the state
  private state$ = new ReplaySubject<EventsBlotterState>(1);
  private eventSub: Subscription = null;
  private timer: any = null;
  private websocketService = inject(GlobalWebsocketService);
  private alertsService = inject(AlertsService);
  private configService = inject(ConfigService);
  private userStorageService = inject(UserStorageService);
  private entityMapperService = inject(EntityMapperService);

  constructor() {
    this.setState({ ...DefaultEventsBlotterState, entities: [], headers: [] });
    this.stateChanged = this.state$.asObservable();
    this.loadEventsBlotterHeaders();

    effect(() => {
      const entities = this.entityMapperService.entities();
      this.setState({
        ...DefaultEventsBlotterState,
        entities: entities.entities,
        realEntities: entities.realEntities
      });
    });
  }

  public update(state: EventsBlotterState): void {
    this.setState(state);
  }

  public async runReport(inEntity: string, horizon: string): Promise<void> {
    let entity = this.entityMapperService.getRealEntity(inEntity);
    const uuid = await this.websocketService.sendMessage({
      action: 'apiv2_eventsReport',
      payload: { entity, horizon }
    });
    console.log('Running Events Report for', entity, horizon, uuid);
    const selectedEntity = this.entityMapperService.getDisplayEntity(entity);
    this.setState(
      {
        ...DefaultEventsBlotterState,
        uuid,
        selectedEntity
      },
      false
    );
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      if (this.state.runningState === RunningState.Running) {
        this.cancelReport('Report timed out');
        console.log('Events Report timed out');
        this.alertsService.addAlert({
          type: 'error',
          message: 'Events Report timed out'
        });
        this.ping();
      }
    }, 5 * 60_000);
  }

  public cancelReport(info: string = null) {
    this.unsubscribe();
    this.setState({ ...DefaultEventsBlotterState, info });
    clearTimeout(this.timer);
    this.timer = null;
  }

  /****<PRIVATE>**************************************************************/

  private loadEventsBlotterHeaders(): void {
    this.configService
      .getAgGridConfig()
      .pipe(
        take(1),
        map(config => {
          const setting = config.find(
            c => c.identifier === 'defaultEventsBlotterHeaders'
          );
          const headers = config.find(c => c.identifier === 'headers');
          return mapHeaders(setting, headers);
        })
      )
      .subscribe({
        next: headers => this.setState({ headers })
      });
  }

  private setState(state: EventsBlotterState, emit = true) {
    const ouuid = this.state.uuid;
    const newState = { ...this.state, ...state };
    if (isEqual(newState, this.state)) {
      return;
    }

    this.state = clone(newState, true);
    if (emit) {
      this.state$.next(this.state);
    }
    if (state.uuid && state.uuid !== ouuid) {
      this.subscribeToEventsBlotter(state.uuid);
    }
  }

  private unsubscribe() {
    this.eventSub?.unsubscribe();
    this.eventSub = null;
  }

  private subscribeToEventsBlotter(uuid: string) {
    this.unsubscribe();
    this.setState({
      uuid,
      rows: [],
      nItems: null,
      runningState: RunningState.Running
    });

    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        console.error(
          'Failed to subscribe to Events Blotter - max retries exceeded'
        );
        return;
      }
      this.eventSub?.unsubscribe();
      this.eventSub = this.websocketService
        .getListener({ uuid, take: Infinity })
        .pipe(
          map((row: WSRow) => {
            return {
              ...row.payload,
              uuid: row.uuid
            } as Row;
          })
        )
        .subscribe({
          next: r => {
            if (r.error) {
              this.handleError(r, true);
            } else if (r.warning) {
              this.handleError(r, false);
            } else if (r.info) {
              this.handleInfo(r);
            } else if (r.item) {
              this.handleData(r);
            }
          },
          complete: async () => {
            console.log('Events Blotter subscription completed');
            await sleep(500);
            resubscribe(iteration + 1);
          },
          error: async err => {
            console.error(err);
            await sleep(500);
            resubscribe(iteration + 1);
          }
        });
    };

    resubscribe(0);
  }

  private handleError(row: Row, abort: boolean) {
    if (abort || (row.warning && row.message === 'No events')) {
      this.unsubscribe();
      this.setState(DefaultEventsBlotterState);
    }
    this.alertsService.addAlert({
      type: row.error ? 'error' : 'warning',
      message: row.message || 'There was a problem running the events report',
      config: {
        closeButton: true,
        tapToDismiss: true,
        timeOut: 3000
      }
    });
    this.ping();
  }

  private handleInfo(row: Row) {
    this.setState({ info: row.message });
  }

  private handleData(row: Row) {
    const curr = this.state.rows;
    const rows = [...curr, row.item];
    let runningState = this.state.runningState;
    let info = this.state.info;
    if (rows.length === row.nItems) {
      this.unsubscribe();
      runningState = RunningState.Idle;
      info = 'Report loaded';
      this.ping();
    }
    this.setState({ rows, nItems: row.nItems, info, runningState });
  }

  private ping(): void {
    this.userStorageService.ping();
  }
}
